<template>
    <div class="overview dashboard-wrapper" :class="{ 'is-mobile': isMobile }">
      <particle-background />
      <!-- 错误通知 -->
      <error-notification
        v-if="showErrorNotification"
        :key="errorNotificationKey"
        :title="errorNotification.title"
        :description="errorNotification.description"
        :type="errorNotification.type"
        :robot-id="selectedRobot ? selectedRobot.id : null"
        :ignore-default-seconds="Math.max(1, Math.floor(alertInfoIgnoreDuration / 1000))"
        :duration="5000"
        @close="closeErrorNotification"
        @ignore="handleIgnoreError"
      />

      <!-- 直接显示详情组件 -->
      <div class="detail-wrapper" id="monitorContainer">
        <detail-header
          :robots="robots"
          :selected-robot="selectedRobot"
          :view-mode="viewMode"
          :data-source="dataSource"
          @switch-robot="switchRobot"
          @toggle-view="toggleView"
          @toggle-data-source="toggleDataSource"
        />
        <!-- 实时状态视图 -->
        <robot-detail 
          v-if="viewMode === 'status' && dataSource === 'realtime' && isRobotOnline && selectedRobot"
          :robot-data="robotDetailData"
          :network-card-data="networkCardData"
          :cpu-data="cpuData"
          :memory-data="memoryData"
          :disk-data="diskData"
          :mileage-data="mileageData"
          :methane-data="methaneData"
          :collision-data="collisionData"
          class="detail-content" />
        
        <!-- 历史状态查询 -->
        <status-history-view
          v-else-if="viewMode === 'status' && dataSource === 'history' && selectedRobot"
          :robot-code="selectedRobot.code"
          :robot-status="selectedRobot.status"
          class="detail-content" />
        <!-- 实时错误监控 -->
        <error-monitor
          v-else-if="viewMode === 'error' && dataSource === 'realtime' && isRobotOnline && selectedRobot"
          :error-data="errorData"
          @delete-record="handleDeleteRecord"
          class="detail-content" />
        
        <!-- 历史错误查询 -->
        <history-data-view
          v-else-if="viewMode === 'error' && dataSource === 'history' && selectedRobot"
          :robot-code="selectedRobot.code"
          :robot-status="selectedRobot.status"
          :active-tab="'device_error'"
          class="detail-content" />

        <!-- 紧急通知历史查询 -->
        <emergency-history-view
          v-else-if="viewMode === 'emergency' && selectedRobot"
          :robot-code="selectedRobot.code"
          :robot-status="selectedRobot.status"
          class="detail-content" />

        
        <!-- 未选择机器人提示 -->
        <div v-else class="no-robot-selected">
          <div class="prompt-container">
            <div class="robot-icon-wrapper">
              <div class="pulse-ring"></div>
              <div class="pulse-ring delay-1"></div>
              <div class="pulse-ring delay-2"></div>
              <div class="robot-icon">
                <img :src="require('@/assets/images/robotCommon/robot-2.png')" alt="Robot" class="robot-image" />
              </div>
            </div>
            <div class="prompt-title">{{ $translate('dashboard_selectRobotTitle') }}</div>
            <div class="prompt-subtitle">{{ $translate('dashboard_selectRobotSubtitle') }}</div>
          </div>
        </div>
      </div>

    </div>
</template>
  
  <script>
  import RobotDetail from './module/detail/RobotDetail.vue'
  import ParticleBackground from '@/components/ParticleBackground/index.vue'
  import DetailHeader from './module/header/DetailHeader.vue'
  import ErrorMonitor from './module/error/ErrorMonitor.vue'
  import HistoryDataView from './module/history/HistoryDataView.vue'
  import StatusHistoryView from './module/history/StatusHistoryView.vue'
  import EmergencyHistoryView from './module/history/EmergencyHistoryView.vue'
  import ErrorNotification from './module/error/ErrorNotification.vue'
  import api from "../../../api/index.js";
  import { getTenantId, checkAndFetchMenuList } from "@/libs/util";
  import { sendMsg, connectWithUrl } from '@/utils/websocket'
  import { buildNotificationContent } from './module/error/notificationContent'
  import {
    beginAlertNotification,
    closeAlertNotification,
    createAlertNotificationState,
    muteAlertNotification
  } from './module/error/alertNotificationControl'

  export default {
    name: 'RobotOverview',
    components: {
      RobotDetail,
      ParticleBackground,
      DetailHeader,
      ErrorMonitor,
      HistoryDataView,
      StatusHistoryView,
      EmergencyHistoryView,
      ErrorNotification
    },
    data() {
      return {
        currentTime: '',
        isMobile: window.innerWidth <= 768,
        selectedRobot: {},
        robotDetailData: [], // 存储实时参数数据数组
        networkCardData: { nicList: [] }, // 存储网卡信息数据
        cpuData: {}, // 存储CPU信息数据
        memoryData: {}, // 存储内存信息数据
        diskData: {}, // 存储磁盘信息数据
        mileageData: {}, // 存储里程状态数据
        methaneData: {}, // 存储甲烷状态数据
        collisionData: {}, // 存储碰撞检测数据
        robots: [],
        userId:this.$store.state.user.userInfo.userId,
        tenantId:getTenantId(),
        viewMode: 'status', // 'status' | 'error'
        dataSource: 'realtime', // 'realtime' | 'history'
        errorData: {
          device_online: [],
          device_error: [],
          running_error: []
        },
        lastDeviceErrorNotifyTime: 0, // 上次设备错误通知时间
        lastRunningErrorNotifyTime: 0, // 上次运行错误通知时间
        notifyInterval: 10000, // 通知间隔时间（毫秒），默认60秒
        showErrorNotification: false,
        errorNotification: {
          title: '',
          description: '',
          type: 'warning',
          sourceType: ''
        },
        errorNotificationKey: 0, // 用于强制重新创建组件
        ignoredRobotErrors: {}, // 记录已忽略的机器人错误，格式: { robotId: { device: true, running: true } }
        monitorSocket: null, // 监控 WebSocket 实例
        ...createAlertNotificationState()
      }
    },
    computed: {
      isRobotOnline() {
        return this.selectedRobot && this.selectedRobot.status === 'online';
      }
    },
    methods: {
      // 关闭所有已存在的 WebSocket（避免进入 monitor 页面后重复连接/消息串台）
      closeAllSockets() {
        try {
          if (window._globalWebsockets && Array.isArray(window._globalWebsockets)) {
            window._globalWebsockets.forEach((ws) => {
              try {
                if (ws && ws.readyState !== WebSocket.CLOSED) {
                  ws.close();
                }
              } catch (e) {
                // ignore
              }
            });
            window._globalWebsockets = [];
          }
        } catch (e) {
          // ignore
        }
      },
      updateTime() {
        const now = new Date()
        this.currentTime = now.toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        })
      },
      handleResize() {
        this.isMobile = window.innerWidth <= 768
      },
      // 模拟数据更新
      updateRobotData() {
        
      },
      // 初始化 WebSocket 连接
      initWebSocket(callback) {
        connectWithUrl(
          '/monitor',
          'monitor', // 唯一 key
          JSON.stringify({
            "type": "init",
            "data": this.userId,
            "language": this.$store.state.app.language,
            "tenantId": this.tenantId
          }), // 不需要初始消息
          (data) => {
            this.handleWebSocketMessage(data);
          },
          (socket) => {
            this.monitorSocket = socket;
            console.log('Monitor WebSocket 连接成功');
            callback && callback();
          },
          (error) => {
            console.error('Monitor WebSocket 连接失败:', error);
            callback && callback();
          }
        );
      },
      // 处理 WebSocket 消息
      handleWebSocketMessage(message) {
        try {
          const data = typeof message === 'string' ? JSON.parse(message) : message;

          switch(data.type) {
            case 'init_success':
              console.log('WebSocket 初始化成功');
            break;
            case 'device_online':
              // 设备在线状态推送，离线设备优先显示
              if (data.data && Array.isArray(data.data)) {
                // 分离在线和离线设备
                const offlineDevices = data.data.filter(item => item.status !== 0);
                const onlineDevices = data.data.filter(item => item.status === 0);
                // 离线设备排在前面
                this.errorData.device_online = [...offlineDevices, ...onlineDevices];
              }
            break;  
            case 'device_error':
              // 设备错误推送
              if (data.data && Array.isArray(data.data)) {
                this.errorData.device_error = data.data;
                // 如果有严重错误，可以自动切换到错误监控视图
                const hasFatalError = data.data.some(item => item.errLevel === 3);
                const now = Date.now();
                const robotId = this.selectedRobot ? this.selectedRobot.id : null;
                const isIgnored = robotId && this.ignoredRobotErrors[robotId]?.device;
                
                if (hasFatalError && this.viewMode === 'status' && 
                    (now - this.lastDeviceErrorNotifyTime) > this.notifyInterval &&
                    !isIgnored) {
                  this.showErrorNotificationDialog(
                    this.$translate('dashboard_errorNotificationDeviceTitle'),
                    this.$translate('dashboard_errorNotificationDeviceDesc'),
                    'warning'
                  );
                  this.lastDeviceErrorNotifyTime = now;
                }
              }
            break;
            case 'running_error':
              // 运行错误推送
              if (data.data && Array.isArray(data.data)) {
                this.errorData.running_error = data.data;
                // 如果有严重错误，可以自动切换到错误监控视图
                const hasFatalError = data.data.some(item => item.errLevel === 3);
                const now = Date.now();
                const robotId = this.selectedRobot ? this.selectedRobot.id : null;
                const isIgnored = robotId && this.ignoredRobotErrors[robotId]?.running;
                
                if (hasFatalError && this.viewMode === 'status' && 
                    (now - this.lastRunningErrorNotifyTime) > this.notifyInterval &&
                    !isIgnored) {
                  this.showErrorNotificationDialog(
                    this.$translate('dashboard_errorNotificationRunningTitle'),
                    this.$translate('dashboard_errorNotificationRunningDesc'),
                    'error'
                  );
                  this.lastRunningErrorNotifyTime = now;
                }
              }
            break;
            case 'running_param':
              // 实时状态参数推送
              if (data.robotCode === this.selectedRobot.code && data.data && Array.isArray(data.data)) {
                this.robotDetailData = data.data;
              }
            break;
            case 'network_card_info':
              // 网卡信息推送
              if (data.data) {
                this.networkCardData = data.data;
              }
            break;
            case 'cpu_info':
              // CPU信息推送
              if (data.robotCode === this.selectedRobot.code && data.data) {
                this.cpuData = data.data;
              }
            break;
            case 'memory_info':
              // 内存信息推送
              if (data.robotCode === this.selectedRobot.code && data.data) {
                this.memoryData = data.data;
              }
            break;
            case 'disk_info':
              // 磁盘信息推送
              if (data.robotCode === this.selectedRobot.code && data.data) {
                this.diskData = data.data;
              }
            break;
            case 'robot_status':
              // 机器人状态推送（包含里程信息）
              if (data.robotCode === this.selectedRobot.code && data.data) {
                this.mileageData = data.data;
              }
            break;
            case 'methane_info':
              // 甲烷信息推送
              if (data.robotCode === this.selectedRobot.code && data.data) {
                this.methaneData = data.data;
              }
            break;
            case 'collision_info':
              // 碰撞检测信息推送
              if (data.robotCode === this.selectedRobot.code && data.data) {
                this.collisionData = data.data;
              }
            break;
            case 'online':
              // 机器人上线
              if (data.robotCode) {
                this.handleRobotStatusChange(data.robotCode, 'online');
              }
            break;
            case 'offline':
              // 机器人离线
              if (data.robotCode) {
                this.handleRobotStatusChange(data.robotCode, 'offline');
              }
            break;
            case 'emergency_info':
            case 'alert_info':
              if (data.data) {
                this.handleEmergencyNotification(data)
              }
            break;
            default:
              break;
          }
        } catch (error) {
            console.error('处理 WebSocket 消息出错:', error);
        }
      },
      handleEmergencyNotification(data) {
        const sourceType = data.type === 'alert_info' ? 'alert_info' : 'emergency_info'

        if (sourceType === 'alert_info' && !beginAlertNotification(this)) return

        const notice = buildNotificationContent(
          sourceType,
          data,
          key => this.$translate(key)
        )
        this.showErrorNotificationDialog(
          notice.title,
          notice.description,
          'emergency',
          sourceType === 'alert_info' ? 'alert_info' : ''
        )
      },
      formatTime(timestamp) {
        if (!timestamp) return '-';
        const date = new Date(timestamp);
        return date.toLocaleString();
      },
      toggleView(mode) {
        if (mode) {
          this.viewMode = mode;
        } else {
          // This part of the logic might need adjustment based on desired toggle behavior
          if (this.viewMode === 'status') {
            this.viewMode = 'error';
          } else if (this.viewMode === 'error') {
            this.viewMode = 'emergency';
          } else {
            this.viewMode = 'status';
          }
        }
      },
      toggleDataSource() {
        // 切换数据来源：实时 <-> 历史
        this.dataSource = this.dataSource === 'realtime' ? 'history' : 'realtime';
        
        // 如果切换到实时监控，且当前是 emergency 模式，则重置为 status
        if (this.dataSource === 'realtime' && this.viewMode === 'emergency') {
          this.viewMode = 'status';
        }
      },
      switchRobot(robot) {
        this.selectedRobot = robot;
        this.robotDetailData = []; // 清空实时参数数据
        this.networkCardData = { nicList: [] }; // 清空网卡数据
        this.cpuData = {}; // 清空CPU数据
        this.memoryData = {}; // 清空内存数据
        this.diskData = {}; // 清空磁盘数据
        this.mileageData = {}; // 清空里程数据
        this.collisionData = {}; // 清空碰撞数据
        this.methaneData = {}; // 清空甲烷数据
        // 清空错误数据
        this.errorData = {
          device_online: [],
          device_error: [],
          running_error: []
        };
        // 清除当前机器人的忽略状态（下次进入会再次提示）
        if (this.ignoredRobotErrors[robot.id]) {
          delete this.ignoredRobotErrors[robot.id];
        }
        
        // 根据机器人状态设置默认数据来源
        if (robot.status === 'online') {
          this.dataSource = 'realtime'; // 在线默认显示实时数据
          // 发送机器人初始化消息
          sendMsg("monitor", JSON.stringify({
            "type": "subscribe",
            "robotCode": robot.code,
            "language": this.$store.state.app.language,
            "tenantId": this.tenantId
          }));
        } else {
          this.dataSource = 'history'; // 离线默认显示历史数据
        }
      },
      showErrorNotificationDialog(title, description, type = 'warning', sourceType = '') {
        this.errorNotification = {
          title,
          description,
          type,
          sourceType
        };
        this.errorNotificationKey = Date.now(); // 更新 key 强制重新创建组件
        this.showErrorNotification = true;
      },
      closeErrorNotification() {
        if (this.errorNotification.sourceType === 'alert_info') {
          closeAlertNotification(this)
        }
        this.showErrorNotification = false;
      },
      handleIgnoreError(robotId, seconds) {
        if (this.errorNotification.sourceType === 'alert_info') {
          muteAlertNotification(this, seconds)
          this.showErrorNotification = false;
          return;
        }
        // 记录当前机器人本次会话忽略的错误类型
        if (!this.ignoredRobotErrors[robotId]) {
          this.ignoredRobotErrors[robotId] = {};
        }
        
        // 根据错误类型设置忽略标记
        if (this.errorNotification.title.includes('设备')) {
          this.ignoredRobotErrors[robotId].device = true;
        } else if (this.errorNotification.title.includes('运行')) {
          this.ignoredRobotErrors[robotId].running = true;
        }
        
        this.showErrorNotification = false;
        console.log(`机器人 ${robotId} 的错误已忽略，下次进入会再次检测`);
      },
      handleRobotStatusChange(robotCode, newStatus) {
        // 查找对应的机器人
        const robot = this.robots.find(r => r.code === robotCode);
        if (!robot) {
          console.log(`未找到机器人: ${robotCode}`);
          return;
        }
        
        const oldStatus = robot.status;
        const statusText = newStatus === 'online' ? this.$translate('dashboard_robotStatusOnlineText') : this.$translate('dashboard_robotStatusOfflineText');
        
        // 更新机器人状态
        robot.status = newStatus;
        console.log(`机器人 ${robot.name} (${robotCode}) 状态变更: ${oldStatus} -> ${newStatus}`);
        
        // 如果是当前选中的机器人，需要特殊处理
        if (this.selectedRobot && this.selectedRobot.code === robotCode) {
          // 更新当前选中机器人的状态
          this.selectedRobot.status = newStatus;
          
          if (newStatus === 'offline') {
            // 机器人离线
            this.$Notice.warning({
              title: this.$translate('dashboard_robotOfflineTitle'),
              desc: this.$translate('dashboard_robotOfflineDesc').replace('{name}', robot.name),
              duration: 6
            });
            
            // 清空实时数据
            this.robotDetailData = [];
            this.networkCardData = { nicList: [] };
            this.cpuData = {};
            this.memoryData = {};
            this.diskData = {};
            this.collisionData = {};
            
            // 如果当前是实时模式，自动切换到历史模式
            if (this.dataSource === 'realtime') {
              this.dataSource = 'history';
            }
          } else {
            // 机器人上线
            this.$Notice.success({
              title: this.$translate('dashboard_robotOnlineTitle'),
              desc: this.$translate('dashboard_robotOnlineDesc').replace('{name}', robot.name),
              duration: 4
            });
            
            // 可以选择自动切换到实时模式，或者让用户手动切换
            // this.dataSource = 'realtime';
            
            // 发送初始化消息以获取实时数据
            if (this.dataSource === 'realtime') {
              sendMsg("monitor", JSON.stringify({
                "type": "init",
                "data": this.userId,
                "robotCode": robot.code,
                "language": this.$store.state.app.language,
                "tenantId": this.tenantId
              }));
            }
          }
        } else {
          // 不是当前选中的机器人，只显示简单通知
        this.$Notice.info({
            title: this.$translate('dashboard_robotStatusInfoTitle').replace('{statusText}', statusText),
            desc: this.$translate('dashboard_robotStatusInfoDesc').replace('{name}', robot.name).replace('{statusText}', statusText),
            duration: 3
          });
        }
      },
      handleDeleteRecord({ type, id }) {
        // 直接从当前数据中删除记录
        if (type === 'device') {
          const index = this.errorData.device_error.findIndex(item => item.id === id);
          if (index > -1) {
            this.errorData.device_error.splice(index, 1);
          }
        } else if (type === 'running') {
          const index = this.errorData.running_error.findIndex(item => item.id === id);
          if (index > -1) {
            this.errorData.running_error.splice(index, 1);
          }
        }
      },
      getRobotList(){
        this.robots = []
        api.getData("robot-server/robot/list/all", this).then(res => {
          if (res.state == 0) {
            this.$Notice.error({
              title: "提示",
              desc: res.msg
            });
          } else {
            if(res.data && res.data.length){
              const onlineList = [],offlineList=[]
              res.data.forEach(j=>{
                const item = {
                  id:j.id,
                  name:j.name,
                  status:j.onlineStatus ? 'online' : 'offline',
                  code:j.code
                }
                j.onlineStatus ? onlineList.push(item) : offlineList.push(item)
              })
              this.robots = [].concat(onlineList).concat(offlineList)
              // 默认选择第一个在线的机器人
              if (onlineList.length) {
                this.switchRobot(onlineList[0]);
              } else if (this.robots.length) {
                this.switchRobot(this.robots[0]);
              }
            }
          }
        });
      }
    },
    mounted() {
      this.updateTime()
      // 更新时间
      this.timeInterval = setInterval(this.updateTime, 1000)
      // 更新机器人数据
      this.dataInterval = setInterval(this.updateRobotData, 5000)
      // 监听窗口大小变化
      window.addEventListener('resize', this.handleResize)
      // 进入 monitor 前，先关闭其他页面残留的 socket
      this.closeAllSockets();
      this.initWebSocket(this.getRobotList)
      // 确保菜单列表已加载
      checkAndFetchMenuList(this.$store)
    },
    beforeDestroy() {
      clearInterval(this.timeInterval)
      clearInterval(this.dataInterval)
      window.removeEventListener('resize', this.handleResize)
      // 关闭所有 WebSocket 连接（包括 monitor）
      this.closeAllSockets();
    }
  }
  </script>
  
  <style lang="less" scoped>
  .overview {
    width: 100%;
    height: 100%;
    background: #001529;
    padding: 10px;
    box-sizing: border-box;
    position: relative;
    z-index: 1;
    overflow: hidden;
    
    .detail-wrapper{
      height: 100%;
      display: flex;
      flex-direction: column;
    }
  }
  
  // 未选择机器人提示样式
  .no-robot-selected {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px;
    
    .prompt-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 30px;
      max-width: 600px;
      padding: 60px 40px;
      background: linear-gradient(135deg, rgba(0, 30, 60, 0.4), rgba(0, 15, 35, 0.6));
      border: 2px solid rgba(0, 240, 255, 0.3);
      border-radius: 20px;
      backdrop-filter: blur(10px);
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4), 0 0 40px rgba(0, 240, 255, 0.2);
      position: relative;
      overflow: hidden;
      
      &::before {
        content: '';
        position: absolute;
        top: -50%;
        left: -50%;
        width: 200%;
        height: 200%;
        background: conic-gradient(
          transparent,
          rgba(0, 240, 255, 0.1),
          transparent 30%
        );
        animation: rotate 6s linear infinite;
      }
      
      .robot-icon-wrapper {
        position: relative;
        width: 150px;
        height: 150px;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1;
        
        .pulse-ring {
          position: absolute;
          width: 100%;
          height: 100%;
          border: 3px solid rgba(0, 240, 255, 0.4);
          border-radius: 50%;
          animation: pulse 3s ease-in-out infinite;
          
          &.delay-1 {
            animation-delay: 0.5s;
          }
          
          &.delay-2 {
            animation-delay: 1s;
          }
        }
        
        .robot-icon {
          font-size: 80px;
          position: relative;
          top:25px;
          z-index: 2;
          animation: float 3s ease-in-out infinite;
          filter: drop-shadow(0 0 20px rgba(0, 240, 255, 0.6));
          .robot-image {
            width: 100%;
            height: 100%;
            object-fit: contain;
          }
        }
      }
      
      .prompt-title {
        font-size: 32px;
        font-weight: 700;
        color: #00f0ff;
        text-shadow: 0 0 20px rgba(0, 240, 255, 0.8);
        letter-spacing: 2px;
        z-index: 1;
      }
      
      .prompt-subtitle {
        font-size: 16px;
        color: rgba(255, 255, 255, 0.7);
        text-align: center;
        line-height: 1.6;
        z-index: 1;
      }
    }
  }
  
  @keyframes pulse {
    0%, 100% {
      transform: scale(0.8);
      opacity: 0.4;
    }
    50% {
      transform: scale(1.2);
      opacity: 0.8;
    }
  }
  
  @keyframes float {
    0%, 100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-15px);
    }
  }
  
  @keyframes rotate {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
  </style>

  <style lang="less">
  @import './module/history/history-view-common.less';
  </style>
  
